import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { enUS, ptBR } from "@clerk/localizations";
import { configureApiClient } from "@package/api-client/client-config";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Toaster, toast } from "sonner";
import { env } from "./env.ts";
import type { LanguageKey } from "./i18n/index.ts";
import i18n from "./i18n/index.ts";
import { LanguageProvider, useLanguage } from "./providers/language-provider.tsx";
import { routeTree } from "./routeTree.gen.ts";
import { getErrorCode } from "./utils/api-error-mapper.ts";
import "./app.css";

const mutationToastMap = new Map<number, string | number>();

const mutationCache = new MutationCache({
	onMutate: (_variables, mutation) => {
		if (mutation.meta?.silent) return;
		const meta = mutation.meta;
		if (meta?.toastMessages?.loading) {
			const toastId = toast.loading(meta.toastMessages.loading);
			mutationToastMap.set(mutation.mutationId, toastId);
		}
	},
	onSuccess: (_data, _variables, _context, mutation) => {
		if (mutation.meta?.silent) return;
		const meta = mutation.meta;
		const toastId = mutationToastMap.get(mutation.mutationId);
		mutationToastMap.delete(mutation.mutationId);
		if (meta?.toastMessages?.success) {
			toast.success(meta.toastMessages.success, { id: toastId });
		} else if (toastId) {
			toast.dismiss(toastId);
		}
	},
	onError: (error, _variables, _context, mutation) => {
		if (mutation.meta?.silent) return;
		const meta = mutation.meta;
		const toastId = mutationToastMap.get(mutation.mutationId);
		mutationToastMap.delete(mutation.mutationId);
		const errorCode = getErrorCode(error);
		const errorCodeKey = errorCode ? `errors.api.${errorCode}` : undefined;

		let message: string;
		if (errorCodeKey && i18n.exists(errorCodeKey)) {
			message = i18n.t(errorCodeKey);
		} else {
			message = meta?.toastMessages?.error ?? i18n.t("errors.api.INTERNAL_ERROR");
		}
		toast.error(message, { id: toastId });
	},
});

const queryClient = new QueryClient({
	mutationCache,
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60,
			gcTime: 1000 * 60 * 5,
			retry: 2,
			refetchOnWindowFocus: false,
		},
		mutations: {
			retry: false,
		},
	},
});

const router = createRouter({
	routeTree,
	context: {
		queryClient,
		get_token: undefined!,
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const clerkLocalizationMap: Record<LanguageKey, typeof enUS | typeof ptBR> = {
	en_US: enUS,
	pt_BR: ptBR,
};

function SetupQueryClient() {
	const { getToken, isLoaded } = useAuth();

	useEffect(() => {
		if (isLoaded) {
			configureApiClient({
				baseURL: env.VITE_BACKEND_API_URL,
				getToken,
			});
		}
	}, [isLoaded, getToken]);

	if (!isLoaded) {
		return (
			<div className="min-h-screen bg-base-100 flex items-center justify-center">
				<span className="loading loading-spinner loading-lg" />
			</div>
		);
	}

	return (
		<QueryClientProvider client={queryClient}>
			<RouterProvider router={router} context={{ get_token: getToken }} />
			<Toaster
				position="top-right"
				expand={true}
				richColors={true}
				closeButton={true}
				duration={4000}
				offset="4.5rem"
				toastOptions={{
					className: "!bg-base-100 !text-base-content !border-base-300 md:!mt-0",
					style: {
						backgroundColor: "hsl(var(--b1))",
						color: "hsl(var(--bc))",
						border: "1px solid hsl(var(--b3))",
					},
				}}
			/>
			<ReactQueryDevtools buttonPosition="bottom-right" />
		</QueryClientProvider>
	);
}

function ClerkProviderWithLocalization({ children }: { children: React.ReactNode }) {
	const { currentLanguage } = useLanguage();
	return (
		<ClerkProvider
			localization={clerkLocalizationMap[currentLanguage]}
			publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}
			afterSignOutUrl="/"
			signInUrl="/sign-in"
			signUpUrl="/sign-up"
			signInFallbackRedirectUrl="/app"
			signUpFallbackRedirectUrl="/app"
		>
			{children}
		</ClerkProvider>
	);
}

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<StrictMode>
			<LanguageProvider>
				<ClerkProviderWithLocalization>
					<SetupQueryClient />
				</ClerkProviderWithLocalization>
			</LanguageProvider>
		</StrictMode>,
	);
}
